import { useNavigate } from 'react-router-dom';

import { SubscriptionFlagsNames } from '~/api/Customer';
import { usePushNotifications } from '~/hooks/usePushNotifications';
import { AnalyticsEvent, analyticsService } from '~/services/analytics';

import { mainStore, orderStore, userStore } from '~/stores';

type UseOnboardingHandlersParams = {
  currentStep: number;
  stepsNumber: number;
  handleChangeScreenIndex: (index: number) => void;
  handleTimerAlive: (alive: boolean) => void;
};

export const useOnboardingHandlers = ({
  currentStep,
  stepsNumber,
  handleChangeScreenIndex,
  handleTimerAlive,
}: UseOnboardingHandlersParams) => {
  const navigate = useNavigate();
  const { updateDeviceAndSendTags } = usePushNotifications();

  const isLatestStep = currentStep === stepsNumber - 1;

  const subscribeToNotifications = (firstName = '', email = '') => {
    updateDeviceAndSendTags(firstName, email, [
      { name: SubscriptionFlagsNames.NEWS, enabled: true },
      { name: SubscriptionFlagsNames.REMINDERS, enabled: true },
      { name: SubscriptionFlagsNames.RECOMMENDATIONS, enabled: true },
      { name: SubscriptionFlagsNames.SPECIAL_DEALS, enabled: true },
      { name: SubscriptionFlagsNames.NEWS_EMAIL, enabled: true },
      { name: SubscriptionFlagsNames.REMINDERS_EMAIL, enabled: true },
      { name: SubscriptionFlagsNames.RECOMMENDATIONS_EMAIL, enabled: true },
      { name: SubscriptionFlagsNames.SPECIAL_DEALS_EMAIL, enabled: true },
    ]);
  };

  const sendOnboardingSuccessful = () => {
    mainStore.sendToRN('sendTags', { onboarding_succesfull: true });
    mainStore.sendToRN('sendTags', {
      onboarding_succesfull_date: Math.floor(Date.now() / 1000),
    });
  };

  const navigateHome = () => {
    navigate('/', { replace: true, state: { isShowDeliveryModal: true } });
  };

  const handleLaterPress = () => {
    if (isLatestStep) {
      const skipPayload = {
        answer: 'skip',
        type: 'no_store',
        postcode: userStore.deliveryAddress?.zip || '',
        lat: userStore.deliveryAddress?.coordinates.lat || '',
        lon: userStore.deliveryAddress?.coordinates.lng || '',
      };
      analyticsService.track(
        AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED,
        skipPayload,
      );
      analyticsService.track(
        AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI,
        skipPayload,
      );
      handleTimerAlive(false);
      mainStore.setOnboardingSeen(true);
      subscribeToNotifications();
      sendOnboardingSuccessful();
      navigateHome();
    } else {
      handleChangeScreenIndex(stepsNumber - 1);
    }
  };

  const handleSubmitForm = ({ name = '', email = '' }) => {
    const nextPayload = {
      answer: 'next',
      type: 'no_store',
      postcode: userStore.deliveryAddress?.zip || '',
      lat: userStore.deliveryAddress?.coordinates.lat || '',
      lon: userStore.deliveryAddress?.coordinates.lng || '',
    };
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED,
      nextPayload,
    );
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_SUBSCRIBE_DIALOG_CLICKED_BI,
      nextPayload,
    );
    const onboardingCompletePayload = {
      eta: orderStore.etaCalculation?.duration.min || 0,
      type: 'passed',
    };
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_COMPLETE,
      onboardingCompletePayload,
    );
    analyticsService.track(
      AnalyticsEvent.ONBOARDING_COMPLETE_BI,
      onboardingCompletePayload,
    );
    mainStore.setOnboardingSeen(true);

    const { middleName, firstName, lastName } = userStore.splitFullName(name);

    if (email) {
      mainStore.sendToRN('setOneSignalUserEmail', email);
    }

    userStore.setDraftUserData({ middleName, firstName, lastName, email });
    subscribeToNotifications(
      `${firstName || ''} ${middleName || ''} ${lastName || ''}`,
      email,
    );

    navigateHome();
  };

  const handleNextPress = () => {
    if (isLatestStep) {
      handleSubmitForm({ name: '', email: '' });
      return;
    }
    handleTimerAlive(false);
    handleChangeScreenIndex(currentStep + 1);
  };

  const handlePrevPress = () => {
    handleTimerAlive(false);
    handleChangeScreenIndex(currentStep - 1);
  };

  return {
    isLatestStep,
    handleLaterPress,
    handleNextPress,
    handlePrevPress,
    handleSubmitForm,
    subscribeToNotifications,
  };
};
